import { FieldTypes } from '../constants/FieldTypes'
import { stateOptions, stateOptionsWithTerritories } from '../constants/states'
import { FieldMapValue } from '../types'
import {
  AlternateField,
  CheckboxField,
  DropdownField,
  EmailField,
  FieldMap,
  PhoneField,
  TextAreaField,
  TextField,
} from '../types/forms'
import { addressToString } from '../utils/data'
import { addFieldRow } from './utils'

export const stateField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'State',
  searchable: true,
  options: stateOptions,
}

export const stateFieldWithTerritories: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'State / Territory',
  searchable: true,
  options: stateOptionsWithTerritories,
}

export const addressField: FieldMap = {
  name: 'Address',
  toName: addressToString,
  // labels: [{ text: 'Address' }],
  children: {
    streetAddress: {
      placeholder: 'Line 1',
      type: FieldTypes.TEXT,
      row: {
        index: 0,
        flex: 2,
      },
    },
    streetAddress2: {
      placeholder: 'Line 2',
      type: FieldTypes.TEXT,
      optional: true,
      row: {
        index: 0,
        flex: 1,
      },
    },
    city: {
      placeholder: 'City',
      type: FieldTypes.TEXT,
      row: {
        index: 1,
        flex: 2,
      },
    },
    state: addFieldRow(stateFieldWithTerritories, { index: 1, flex: 2 }),
    zip: {
      placeholder: 'ZIP Code',
      type: FieldTypes.TEXT,
      row: {
        index: 1,
        flex: 1,
      },
    },
  },
}

const nineDigitZipFormat = (v: any) => {
  if (!v) return v
  if (v.length > 5) return `${v.substring(0, 5)}-${v.substring(5)}`
  return v
}
export const nineDigitZipField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: '9-digit ZIP Code',
  validate: v => {
    if (v && v.length !== 9) return 'Should be 9 digits'
    return undefined
  },
  format: nineDigitZipFormat,
  parse: v => {
    if (v) return v.replace(/-/g, '')
    return v
  },
}
const nineDigitAddressToString = (address: FieldMapValue) => {
  const { streetAddress, streetAddress2, city, state, zip } = address
  const missing = []
  if (!streetAddress) missing.push('street address')
  if (!city) missing.push('city')
  if (!state) missing.push('state')
  if (!zip) missing.push('zip code')
  if (missing.length) return `Missing ${missing.join(', ')}`
  const formattedZip = nineDigitZipFormat(zip)
  return `${streetAddress}${streetAddress2 ? `, ${streetAddress2}` : ''}, ${city}, ${state} ${formattedZip}`
}
export const nineDigitZipAddress: FieldMap = {
  ...addressField,
  toName: nineDigitAddressToString,
  children: {
    ...addressField.children,
    zip: nineDigitZipField,
  },
}

export const faxField: PhoneField = {
  type: FieldTypes.PHONE,
  placeholder: 'Fax',
  optional: true,
}

export const alternateFaxField: AlternateField = {
  type: FieldTypes.ALTERNATE,
  placeholder: 'Fax',
  optional: true,
  fieldType: FieldTypes.PHONE,
}

export const fnameField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'First Name',
}
export const lnameField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Last Name',
}

export const emailField: EmailField = {
  type: FieldTypes.EMAIL,
  placeholder: 'Email',
}

export const alternateEmailField: AlternateField = {
  type: FieldTypes.ALTERNATE,
  placeholder: 'Email',
  fieldType: FieldTypes.EMAIL,
}
export const notesField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Notes...',
  optional: true,
}

export const getBooleanHistoryField = (
  placeholder: string,
  noCondition?: boolean,
): CheckboxField => ({
  placeholder,
  type: FieldTypes.CHECKBOX,
  inputType: 'switch',
  size: 'md',
  condition: noCondition ? undefined : v => !v?.notApplicable && !v?.declineToAnswer,
})

export const getPleaseSpecifyField = (
  conditionalOn: string,
  optional?: boolean,
): TextAreaField => ({
  placeholder: 'Please specify...',
  type: FieldTypes.TEXTAREA,
  condition: v => v?.[conditionalOn],
  optional,
})

export const notApplicableField: CheckboxField = {
  placeholder: 'Not applicable',
  size: 'md',
  type: FieldTypes.CHECKBOX,
}

export const declineToAnswerField: CheckboxField = {
  placeholder: 'Decline to answer',
  size: 'md',
  type: FieldTypes.CHECKBOX,
}
